import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import type { EventData } from './validation';
import type { PartnerData } from './validation';
import type { LogoData } from './validation';
import type { PlayerData } from './validation';
import type { FinancialTransactionData, BudgetItemData, BudgetAdjustmentData } from './validation';

const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').trim();
const supabaseAnonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(`Missing Supabase environment variables: ${!supabaseUrl ? 'VITE_SUPABASE_URL' : ''} ${!supabaseAnonKey ? 'VITE_SUPABASE_ANON_KEY' : ''}`.trim());
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

export async function fetchPaginated<T = Record<string, unknown>>(
  table: string,
  select: string,
  filters: { column: string; op: 'eq' | 'in' | 'gt'; value: unknown }[],
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = [];
  let from = 0;
  while (true) {
    let q = supabase.from(table).select(select) as any;
    for (const f of filters) {
      if (f.op === 'eq') q = q.eq(f.column, f.value);
      else if (f.op === 'in') q = q.in(f.column, f.value as unknown[]);
      else if (f.op === 'gt') q = q.gt(f.column, f.value);
    }
    const { data, error } = await q.range(from, from + pageSize - 1);
    if (error) { console.warn(`Paginated fetch ${table} error:`, error.message); break; }
    if (!data || data.length === 0) break;
    all.push(...(data as T[]));
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

export async function fetchHoleScoresPaginated(
  tournamentId: string,
  playerIds: string[],
  pageSize = 1000,
): Promise<{ player_id: string; round: number; hole: number; strokes: number; score_status: string | null }[]> {
  return fetchPaginated<{ player_id: string; round: number; hole: number; strokes: number; score_status: string | null }>(
    'hole_scores',
    'player_id, round, hole, strokes, score_status',
    [
      { column: 'tournament_id', op: 'eq', value: tournamentId },
      { column: 'player_id', op: 'in', value: playerIds },
      { column: 'hole', op: 'gt', value: 0 },
    ],
    pageSize,
  );
}

// Function to validate UUID format
export const isValidUUID = (uuid: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

export async function getTournaments() {
  return await supabase
    .from('tournaments')
    .select('*')
    .order('start_date', { ascending: true });
}

export async function createTournament(data: {
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  capacity: number;
  registration_fee: number;
  prize_pool: number;
  description?: string;
  venue?: string;
  registration_open_date?: string | null;
  registration_close_date?: string | null;
  scoring_method?: string;
}) {
  const { scoring_method: _, ...payload } = data;
  return await supabase
    .from('tournaments')
    .insert([payload])
    .select()
    .single();
}

export async function updateTournament(
  id: string,
  data: {
    name?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    capacity?: number;
    registration_fee?: number;
    prize_pool?: number;
    description?: string;
    venue?: string;
    registration_open_date?: string | null;
    registration_close_date?: string | null;
    scoring_method?: string;
  }
) {
  const { scoring_method: _, ...payload } = data;
  return await supabase
    .from('tournaments')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
}

export async function deleteTournament(id: string) {
  return await supabase
    .from('tournaments')
    .delete()
    .eq('id', id);
}

export async function getTournamentEvents(tournamentId: string) {
  // Validate UUID format before making the request
  if (!isValidUUID(tournamentId)) {
    return { data: [], error: new Error('Invalid tournament ID format') };
  }
  
  return await supabase
    .from('events')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('date', { ascending: true });
}

export async function createEvent(data: EventData) {
  // Validate UUID format before making the request
  if (!isValidUUID(data.tournament_id)) {
    return { data: null, error: new Error('Invalid tournament ID format') };
  }
  
  return await supabase
    .from('events')
    .insert([data])
    .select()
    .single();
}

export async function updateEvent(id: string, data: EventData) {
  // Validate UUID format before making the request
  if (!isValidUUID(data.tournament_id)) {
    return { data: null, error: new Error('Invalid tournament ID format') };
  }
  
  return await supabase
    .from('events')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

export async function deleteEvent(id: string) {
  return await supabase
    .from('events')
    .delete()
    .eq('id', id);
}

export async function registerPartner(data: PartnerData) {
  return await supabase
    .from('partners')
    .insert([data])
    .select()
    .single();
}

async function deletePartner(id: string) {
  return await supabase
    .from('partners')
    .delete()
    .eq('id', id);
}

export async function getContentSections(page: string) {
  return await supabase
    .from('content_sections')
    .select(`
      *,
      content_items (*)
    `)
    .eq('page', page);
}

export async function createContentItem(data: {
  title: string;
  type: string;
  content: string;
  status: string;
  section_id: string;
}) {
  return await supabase
    .from('content_items')
    .insert([data])
    .select()
    .single();
}

export async function updateContentItem(
  id: string,
  data: {
    title: string;
    type: string;
    content: string;
    status: string;
    section_id: string;
  }
) {
  return await supabase
    .from('content_items')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

export async function deleteContentItem(id: string) {
  return await supabase
    .from('content_items')
    .delete()
    .eq('id', id);
}

// Logo management functions
export async function getLogos() {
  return await supabase
    .from('logos')
    .select('*')
    .order('created_at', { ascending: false });
}

export async function createLogo(data: LogoData) {
  return await supabase
    .from('logos')
    .insert([data])
    .select()
    .single();
}

export async function updateLogo(id: string, data: LogoData) {
  return await supabase
    .from('logos')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

export async function deleteLogo(id: string) {
  return await supabase
    .from('logos')
    .delete()
    .eq('id', id);
}

// Player management functions
export async function getPlayers() {
  return await supabase
    .from('players')
    .select('*')
    .order('created_at', { ascending: false });
}

async function getPlayerById(id: string) {
  return await supabase
    .from('players')
    .select('*')
    .eq('id', id)
    .single();
}

async function createPlayer(data: PlayerData) {
  return await supabase
    .from('players')
    .insert([data])
    .select()
    .single();
}

export async function updatePlayer(id: string, data: Partial<PlayerData>) {
  return await supabase
    .from('players')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

export async function deletePlayer(id: string) {
  return await supabase
    .from('players')
    .delete()
    .eq('id', id);
}

export async function updatePlayerStatus(id: string, status: string) {
  return await supabase
    .from('players')
    .update({ status })
    .eq('id', id);
}

export async function updateRegistrationStatus(id: string, registrationStatus: {
  personalInfo: boolean;
  payment: boolean;
  verification: boolean;
}) {
  return await supabase
    .from('players')
    .update({ registration_status: registrationStatus })
    .eq('id', id);
}

// Partner management functions
async function getPartners() {
  return await supabase
    .from('partners')
    .select('*')
    .order('created_at', { ascending: false });
}

export async function getActivePartners() {
  try {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .eq('status', 'active')
      .lte('start_date', today)
      .gte('end_date', today)
      .order('contribution', { ascending: false });

    return { data, error };
  } catch (error) {
    console.error("Error in getActivePartners:", error);
    return { data: [], error: null };
  }
}

async function getPartnerById(id: string) {
  return await supabase
    .from('partners')
    .select('*')
    .eq('id', id)
    .single();
}

async function updatePartner(id: string, data: Partial<PartnerData>) {
  return await supabase
    .from('partners')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

// Financial management functions
export async function getFinancialSummary(tournamentId: string) {
  return await supabase
    .from('financial_summary_view')
    .select('*')
    .eq('tournament_id', tournamentId)
    .single();
}

async function getBudgetStatus(tournamentId: string) {
  return await supabase
    .from('budget_status_view')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('category', { ascending: true });
}

async function getFinancialTransactions(tournamentId: string) {
  return await supabase
    .from('transaction_details_view')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('transaction_date', { ascending: false });
}

async function getTransactionsByType(tournamentId: string, type: 'income' | 'expense') {
  return await supabase
    .from('financial_transactions')
    .select('*')
    .eq('tournament_id', tournamentId)
    .eq('type', type)
    .order('transaction_date', { ascending: false });
}

async function createFinancialTransaction(data: FinancialTransactionData) {
  return await supabase
    .from('financial_transactions')
    .insert([data])
    .select()
    .single();
}

async function updateFinancialTransaction(id: string, data: Partial<FinancialTransactionData>) {
  return await supabase
    .from('financial_transactions')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

async function deleteFinancialTransaction(id: string) {
  return await supabase
    .from('financial_transactions')
    .delete()
    .eq('id', id);
}

async function getBudgetItems(tournamentId: string) {
  return await supabase
    .from('budget_items')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('category', { ascending: true });
}

async function createBudgetItem(data: BudgetItemData) {
  return await supabase
    .from('budget_items')
    .insert([data])
    .select()
    .single();
}

async function updateBudgetItem(id: string, data: Partial<BudgetItemData>) {
  return await supabase
    .from('budget_items')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

async function deleteBudgetItem(id: string) {
  return await supabase
    .from('budget_items')
    .delete()
    .eq('id', id);
}

async function getBudgetAdjustments(budgetItemId: string) {
  return await supabase
    .from('budget_adjustments')
    .select('*')
    .eq('budget_item_id', budgetItemId)
    .order('adjustment_date', { ascending: false });
}

async function createBudgetAdjustment(data: BudgetAdjustmentData) {
  return await supabase
    .from('budget_adjustments')
    .insert([data])
    .select()
    .single();
}

async function getMonthlyFinancialSummary(tournamentId: string) {
  return await supabase
    .from('monthly_financial_summary_view')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('month', { ascending: true });
}

export async function getPartnersByTournament(tournamentId: string) {
  return await supabase
    .from('partners')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('contribution', { ascending: false });
}

export async function getPlayersByTournament(tournamentId: string) {
  return await supabase
    .from('players')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('created_at', { ascending: false });
}

// Player scores functions
export async function getPlayerScores(tournamentId: string) {
  return await supabase
    .from('player_scores')
    .select('*, players(first_name, last_name, country, resident_club, category)')
    .eq('tournament_id', tournamentId)
    .order('date', { ascending: false });
}

export async function createPlayerScore(data: {
  player_id: string;
  tournament_id: string;
  round: number;
  score: number;
  date: string;
  notes?: string;
}) {
  return await supabase
    .from('player_scores')
    .insert([data])
    .select()
    .single();
}

export async function bulkCreatePlayerScores(data: {
  player_id: string;
  tournament_id: string;
  round: number;
  score: number;
  date: string;
  notes?: string;
}[]) {
  return await supabase
    .from('player_scores')
    .insert(data)
    .select();
}

export async function updatePlayerScore(id: string, data: {
  round?: number;
  score?: number;
  date?: string;
  notes?: string;
}) {
  return await supabase
    .from('player_scores')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

export async function deletePlayerScore(id: string) {
  try {
    // First get the player score to find player_id and tournament_id
    const { data: score, error: scoreError } = await supabase
      .from('player_scores')
      .select('player_id, tournament_id')
      .eq('id', id)
      .single();
    
    if (scoreError) {
      console.error('Error fetching score before deletion:', scoreError);
    } else if (score) {
      // Remove any awards associated with this player
      const { error: awardError } = await supabase
        .from('player_awards')
        .delete()
        .eq('player_id', score.player_id)
        .eq('tournament_id', score.tournament_id);
      
      if (awardError) {
        console.error('Error removing player awards:', awardError);
      }
    }
  } catch (err) {
    console.error('Error in pre-delete operations:', err);
  }
  
  return await supabase
    .from('player_scores')
    .delete()
    .eq('id', id);
}

// Award categories functions
export async function getAwardCategories() {
  return await supabase
    .from('award_categories')
    .select('*')
    .order('name', { ascending: true });
}

export async function createAwardCategory(data: {
  name: string;
  description: string;
}) {
  return await supabase
    .from('award_categories')
    .insert([data])
    .select()
    .single();
}

export async function updateAwardCategory(id: string, data: {
  name?: string;
  description?: string;
}) {
  return await supabase
    .from('award_categories')
    .update(data)
    .eq('id', id)
    .select()
    .single();
}

export async function deleteAwardCategory(id: string) {
  return await supabase
    .from('award_categories')
    .delete()
    .eq('id', id);
}

export async function assignPlayerAward(data: {
  player_id: string;
  tournament_id: string;
  award_id?: string | undefined;
  notes?: string;
}) {
  try {
    // If no award_id is provided, remove any existing awards for this player in this tournament
    if (!data.award_id) {
      return await supabase
        .from('player_awards')
        .delete()
        .eq('player_id', data.player_id)
        .eq('tournament_id', data.tournament_id);
    }
    
    // First check if this specific award already exists for this player in this tournament
    const { data: existingAward, error: checkError } = await supabase
      .from('player_awards')
      .select('id')
      .eq('player_id', data.player_id)
      .eq('tournament_id', data.tournament_id)
      .eq('award_id', data.award_id)
      .maybeSingle();
    
    if (checkError && !checkError.message.includes('No rows found')) {
      throw checkError;
    }
    
    if (existingAward) {
      // Update existing award
      return await supabase
        .from('player_awards')
        .update({
          notes: data.notes
        })
        .eq('id', existingAward.id);
    } else {
      // Create new award
      return await supabase
        .from('player_awards')
        .insert([data])
        .select();
    }
  } catch (error) {
    console.error('Error managing player award:', error);
    throw error;
  }
}

export async function getPlayerAwards(playerId: string, tournamentId: string) {
  return await supabase
    .from('player_awards')
    .select('*, award_categories(id, name, description)')
    .eq('player_id', playerId)
    .eq('tournament_id', tournamentId);
}

export async function removePlayerAward(playerId: string, awardId: string) {
  return await supabase
    .from('player_awards')
    .delete()
    .eq('player_id', playerId)
    .eq('award_id', awardId);
}

async function getLeaderboard(tournamentId: string) {
  return await supabase
    .from('leaderboard_view')
    .select('*')
    .eq('tournament_id', tournamentId)
    .order('total_score', { ascending: true });
}

// Function to generate registration numbers for players without one
export async function generateRegistrationNumbers() {
  try {
    // First, get all players without registration numbers
    const { data: players, error } = await supabase
      .from('players')
      .select('id, first_name, last_name')
      .is('registration_number', null);
    
    if (error) throw error;
    
    if (!players || players.length === 0) {
      console.log('No players without registration numbers found');
      return { success: true, message: 'No players needed registration numbers', count: 0 };
    }
    
    // Generate and update registration numbers for each player
    const updates = players.map(async (player) => {
      const regNumber = `IBO-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      
      const { error: updateError } = await supabase
        .from('players')
        .update({ registration_number: regNumber })
        .eq('id', player.id);
      
      if (updateError) {
        console.error(`Error updating player ${player.id}:`, updateError);
        return false;
      }
      
      return true;
    });
    
    // Wait for all updates to complete
    const results = await Promise.all(updates);
    const successCount = results.filter(Boolean).length;
    
    return { 
      success: true, 
      message: `Generated registration numbers for ${successCount} players`, 
      count: successCount 
    };
  } catch (error) {
    console.error('Error generating registration numbers:', error);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Unknown error', 
      count: 0 
    };
  }
}